import { useState, useCallback, useEffect, useRef } from "react";
import { GoogleMap, useJsApiLoader, Polygon } from "@react-google-maps/api";
import { CurrentLocationMarker } from "../CurrentLocationMarker";
import { ProblemLocationMarker } from "../ProblemLocationMarker";
import {
  FORTALEZA_BOUNDARIES,
  isLocationInFortaleza,
} from "@/utils/municipalityBoundaries";

interface LocationMapProps {
  selectedLocation: { lat: number; lng: number } | null;
  onLocationSelect: (location: { lat: number; lng: number }) => void;
  userLocation: { lat: number; lng: number } | null;
  shouldPanToLocation?: boolean;
  forceUserLocationPan?: boolean;
  onLocationValidation?: (isValid: boolean) => void;
  onAddressResolved?: (address: string) => void;
}

const libraries: ("places" | "drawing" | "geometry")[] = ["places"];

// Localização padrão (Centro de Frecheirinha, Ceará)
const defaultCenter = {
  lat: -3.7588,
  lng: -40.8135,
};

export const LocationMap = ({
  selectedLocation,
  onLocationSelect,
  userLocation,
  shouldPanToLocation = false,
  forceUserLocationPan = false,
  onLocationValidation,
  onAddressResolved,
}: LocationMapProps) => {
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [mapCenter, setMapCenter] = useState(selectedLocation || defaultCenter);
  const [isLocationValid, setIsLocationValid] = useState(true);
  const lastPannedLocation = useRef<{ lat: number; lng: number } | null>(null);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
    libraries,
    language: "pt-BR",
    region: "BR",
  });

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      setMap(map);

      // Configurar listener para Street View
      const streetView = map.getStreetView();

      // Listener para quando a posição do Street View mudar
      streetView.addListener("position_changed", () => {
        const position = streetView.getPosition();
        if (position) {
          const location = {
            lat: position.lat(),
            lng: position.lng(),
          };

          // Validar se a localização está dentro dos limites
          const isValid = isLocationInFortaleza(location);
          setIsLocationValid(isValid);

          if (onLocationValidation) {
            onLocationValidation(isValid);
          }

          onLocationSelect(location);
        }
      });
    },
    [onLocationSelect, onLocationValidation],
  );

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  // Atualizar centro do mapa quando selectedLocation for definido inicialmente
  useEffect(() => {
    if (selectedLocation && !lastPannedLocation.current) {
      setMapCenter(selectedLocation);
      if (map) {
        map.panTo(selectedLocation);
        map.setZoom(15);
        lastPannedLocation.current = selectedLocation;
      }
    }
  }, [map, selectedLocation]);

  // Mover o mapa apenas quando userLocation mudar (não quando clicar no mapa)
  useEffect(() => {
    if (map && userLocation) {
      const locationKey = `${userLocation.lat},${userLocation.lng}`;
      const lastKey = lastPannedLocation.current
        ? `${lastPannedLocation.current.lat},${lastPannedLocation.current.lng}`
        : null;

      // Forçar movimento se forceUserLocationPan for true, ou se for uma localização diferente
      if (forceUserLocationPan || locationKey !== lastKey) {
        setMapCenter(userLocation);
        map.panTo(userLocation);
        map.setZoom(17);
        lastPannedLocation.current = userLocation;
      }
    }
  }, [map, userLocation, forceUserLocationPan]);

  // Mover o mapa suavemente quando uma localização for selecionada pela pesquisa
  useEffect(() => {
    if (map && selectedLocation && shouldPanToLocation) {
      const locationKey = `${selectedLocation.lat},${selectedLocation.lng}`;
      const lastKey = lastPannedLocation.current
        ? `${lastPannedLocation.current.lat},${lastPannedLocation.current.lng}`
        : null;

      if (locationKey !== lastKey) {
        setMapCenter(selectedLocation);
        map.panTo(selectedLocation);
        map.setZoom(17);
        lastPannedLocation.current = selectedLocation;
      }
    }
  }, [map, selectedLocation, shouldPanToLocation]);

  const handleMapClick = (e: google.maps.MapMouseEvent) => {
    if (e.latLng) {
      const lat = e.latLng.lat();
      const lng = e.latLng.lng();
      const location = { lat, lng };

      // Validar se a localização está dentro dos limites
      const isValid = isLocationInFortaleza(location);
      setIsLocationValid(isValid);

      if (onLocationValidation) {
        onLocationValidation(isValid);
      }

      onLocationSelect(location);
    }
  };

  // Validar localização inicial se existir
  useEffect(() => {
    if (selectedLocation) {
      const isValid = isLocationInFortaleza(selectedLocation);
      setIsLocationValid(isValid);
      if (onLocationValidation) {
        onLocationValidation(isValid);
      }
    }
  }, [selectedLocation, onLocationValidation]);

  if (!isLoaded) {
    return (
      <div className="relative aspect-[4/3] overflow-hidden rounded-2xl border-0 shadow-lg bg-gray-100 flex items-center justify-center">
        <p className="text-gray-500">Carregando mapa...</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Mensagem de erro quando fora dos limites - ACIMA do mapa */}
      {selectedLocation && !isLocationValid && (
        <div className="mb-3 max-h-48 overflow-y-auto">
          <div className="p-3 bg-red-50 border-2 border-red-200 rounded-xl shadow-lg">
            <div className="flex items-start gap-2 mb-3">
              <span className="text-red-600 text-xl shrink-0">⚠️</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-red-800">
                  Localização fora da área de cobertura
                </p>
                <p className="text-xs text-red-700 mt-1">
                  A localização selecionada está fora dos limites do município
                  de Frecheirinha. Por favor, selecione uma localização dentro
                  da área destacada no mapa.
                </p>
              </div>
            </div>
            <button
              onClick={() => {
                const defaultLocation = {
                  lat: -3.7588,
                  lng: -40.8135,
                };
                onLocationSelect(defaultLocation);
              }}
              className="w-full px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 text-sm"
            >
              <span>📍</span>
              Ir para o centro do município
            </button>
          </div>
        </div>
      )}

      {/* Container do mapa com altura fixa que NÃO muda */}
      <div
        className="relative w-full rounded-2xl overflow-hidden shadow-lg"
        style={{ height: "500px", minHeight: "500px", maxHeight: "500px" }}
      >
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={mapCenter}
          zoom={12}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={handleMapClick}
          options={{
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: true,
            scaleControl: true,
            streetViewControl: true,
            rotateControl: true,
            fullscreenControl: true,
            zoomControlOptions: {
              position: 9, // RIGHT_CENTER
            },
            mapTypeControlOptions: {
              position: 3, // TOP_RIGHT
            },
            streetViewControlOptions: {
              position: 9, // RIGHT_CENTER
            },
            fullscreenControlOptions: {
              position: 7, // RIGHT_TOP
            },
            styles: [
              {
                featureType: "poi",
                elementType: "labels",
                stylers: [{ visibility: "off" }],
              },
            ],
          }}
        >
          {/* Polígono dos limites do município */}
          <Polygon
            paths={FORTALEZA_BOUNDARIES}
            options={{
              fillColor: "hsl(211 75% 50%)",
              fillOpacity: 0.15,
              strokeColor: "hsl(211 75% 30%)",
              strokeOpacity: 0.9,
              strokeWeight: 3,
              clickable: false,
              zIndex: 1,
            }}
          />

          {userLocation && !selectedLocation && (
            <CurrentLocationMarker position={userLocation} />
          )}
          {selectedLocation && (
            <ProblemLocationMarker position={selectedLocation} onAddressResolved={onAddressResolved} />
          )}
        </GoogleMap>
      </div>
    </div>
  );
};
